import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@headlessui/react';
import {
  DocumentArrowUpIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ArrowLeftIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { cfpbFilingApi, hmdaApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import PermissionGate from '../components/PermissionGate';
import clsx from 'clsx';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  UPLOADING: 'bg-blue-100 text-blue-700',
  UPLOADED: 'bg-blue-100 text-blue-700',
  PARSING: 'bg-blue-100 text-blue-700',
  PARSED: 'bg-blue-100 text-blue-700',
  VALIDATING: 'bg-blue-100 text-blue-700',
  VALIDATED: 'bg-indigo-100 text-indigo-700',
  SIGNED: 'bg-purple-100 text-purple-700',
  SUBMITTED: 'bg-amber-100 text-amber-700',
  ACCEPTED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

const VALID_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['UPLOADING'],
  UPLOADING: ['UPLOADED'],
  UPLOADED: ['PARSING'],
  PARSING: ['PARSED'],
  PARSED: ['VALIDATING'],
  VALIDATING: ['VALIDATED', 'REJECTED'],
  VALIDATED: ['SIGNED'],
  SIGNED: ['SUBMITTED'],
  SUBMITTED: ['ACCEPTED', 'REJECTED'],
};

export default function CfpbFilingPage() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Create form state
  const [formYear, setFormYear] = useState<number>(new Date().getFullYear() - 1);
  const [formLei, setFormLei] = useState('');
  const [formInstitutionName, setFormInstitutionName] = useState('');
  const [formValidationId, setFormValidationId] = useState('');

  // Team
  const { teamId } = useTeamId();

  // List filings
  const { data: listData, isLoading } = useQuery({
    queryKey: ['cfpb-filings', teamId],
    queryFn: () => cfpbFilingApi.list(teamId!, { limit: 50 }),
    enabled: !!teamId,
  });
  const filings = listData?.data?.filings || [];

  // HMDA validations — used to populate the Create Filing dropdown so users
  // pick a real validation instead of pasting a UUID by hand.
  const { data: validationsData } = useQuery({
    queryKey: ['hmda-validations-for-cfpb', teamId],
    queryFn: () => hmdaApi.getValidations({ limit: 100 }),
    enabled: !!teamId && showCreateModal,
  });
  const teamValidations = validationsData?.data?.validations || [];

  // Detail
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['cfpb-filing-detail', teamId, selectedId],
    queryFn: () => cfpbFilingApi.get(teamId!, selectedId!),
    enabled: !!teamId && !!selectedId,
  });
  const detail = detailData?.data;

  // Summary counts
  const totalCount = filings.length;
  const draftCount = filings.filter((f: any) => f.status === 'DRAFT').length;
  const validatedCount = filings.filter((f: any) => f.status === 'VALIDATED').length;
  const submittedCount = filings.filter((f: any) => f.status === 'SUBMITTED' || f.status === 'ACCEPTED').length;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: () =>
      cfpbFilingApi.create(teamId!, {
        filingYear: formYear,
        lei: formLei,
        institutionName: formInstitutionName || undefined,
        validationId: formValidationId || undefined,
      }),
    onSuccess: () => {
      toast.success('Filing created');
      queryClient.invalidateQueries({ queryKey: ['cfpb-filings'] });
      setShowCreateModal(false);
      resetForm();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to create filing'),
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      cfpbFilingApi.updateStatus(teamId!, id, { status }),
    onSuccess: () => {
      toast.success('Status updated');
      queryClient.invalidateQueries({ queryKey: ['cfpb-filings'] });
      queryClient.invalidateQueries({ queryKey: ['cfpb-filing-detail'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to update status'),
  });

  // Sign mutation
  const signMutation = useMutation({
    mutationFn: (id: string) => cfpbFilingApi.sign(teamId!, id),
    onSuccess: () => {
      toast.success('Filing signed successfully');
      queryClient.invalidateQueries({ queryKey: ['cfpb-filings'] });
      queryClient.invalidateQueries({ queryKey: ['cfpb-filing-detail'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to sign filing'),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => cfpbFilingApi.delete(teamId!, id),
    onSuccess: () => {
      toast.success('Filing deleted');
      queryClient.invalidateQueries({ queryKey: ['cfpb-filings'] });
      setDeleteConfirm(null);
      if (selectedId === deleteConfirm) setSelectedId(null);
    },
    onError: () => toast.error('Failed to delete filing'),
  });

  const resetForm = () => {
    setFormYear(new Date().getFullYear() - 1);
    setFormLei('');
    setFormInstitutionName('');
    setFormValidationId('');
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const getTransitions = (status: string): string[] => {
    return VALID_TRANSITIONS[status] || [];
  };

  return (
    <FeatureGate feature="cfpb_filing" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">CFPB Filing</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage HMDA submissions to the CFPB filing platform
            </p>
          </div>
          <PermissionGate teamId={teamId} permission="compliance.write">
            <button
              onClick={() => setShowCreateModal(true)}
              disabled={!teamId}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4" />
              Create Filing
            </button>
          </PermissionGate>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-500">Total Filings</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{totalCount}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-500">Draft</p>
            <p className="mt-1 text-2xl font-bold text-gray-600">{draftCount}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-500">Validated</p>
            <p className={`mt-1 text-2xl font-bold ${validatedCount > 0 ? 'text-teal-600' : 'text-gray-900'}`}>{validatedCount}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <p className="text-xs font-medium text-gray-500">Submitted</p>
            <p className={`mt-1 text-2xl font-bold ${submittedCount > 0 ? 'text-green-600' : 'text-gray-900'}`}>{submittedCount}</p>
          </div>
        </div>

        {/* Detail View */}
        {selectedId && (
          <div className="rounded-lg border bg-white">
            <div className="border-b px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedId(null)}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                  </button>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Filing Detail
                      {detail && ` - ${detail.filingYear} / ${detail.lei}`}
                    </h3>
                    {detail?.institutionName && (
                      <p className="text-xs text-gray-400 mt-0.5">{detail.institutionName}</p>
                    )}
                  </div>
                </div>
                {detail && (
                  <span className={clsx('inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium', STATUS_COLORS[detail.status] || STATUS_COLORS.DRAFT)}>
                    {detail.status}
                  </span>
                )}
              </div>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            ) : detail ? (
              <div className="p-4 space-y-6">
                {/* Filing Info */}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500">Filing Year</p>
                    <p className="mt-0.5 text-sm font-medium text-gray-900">{detail.filingYear}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">LEI</p>
                    <p className="mt-0.5 text-sm font-mono text-gray-900">{detail.lei}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Sequence Number</p>
                    <p className="mt-0.5 text-sm text-gray-900">{detail.sequenceNumber || '--'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Created</p>
                    <p className="mt-0.5 text-sm text-gray-900">{formatDate(detail.createdAt)}</p>
                  </div>
                </div>

                {/* Status History Timeline */}
                {detail.statusHistory && detail.statusHistory.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Status History</h4>
                    <div className="mt-3 flow-root">
                      <ul className="-mb-8">
                        {detail.statusHistory.map((entry: any, idx: number) => (
                          <li key={idx}>
                            <div className="relative pb-8">
                              {idx !== detail.statusHistory.length - 1 && (
                                <span className="absolute left-3 top-3 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                              )}
                              <div className="relative flex items-start space-x-3">
                                <div>
                                  <span className={clsx(
                                    'flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white',
                                    STATUS_COLORS[entry.status]?.replace('text-', 'bg-').split(' ')[0] || 'bg-gray-200'
                                  )}>
                                    <PencilSquareIcon className="h-3 w-3 text-white" />
                                  </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', STATUS_COLORS[entry.status] || STATUS_COLORS.DRAFT)}>
                                      {entry.status}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      {formatDate(entry.timestamp || entry.createdAt)}
                                    </span>
                                  </div>
                                  {entry.message && (
                                    <p className="mt-0.5 text-xs text-gray-500">{entry.message}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2 border-t pt-4">
                  {/* Sign button - only when VALIDATED */}
                  {detail.status === 'VALIDATED' && (
                    <button
                      onClick={() => signMutation.mutate(selectedId)}
                      disabled={signMutation.isPending}
                      className="inline-flex items-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                    >
                      {signMutation.isPending ? 'Signing...' : 'Sign Filing'}
                    </button>
                  )}

                  {/* Status transition buttons */}
                  {getTransitions(detail.status)
                    .filter((s) => s !== 'SIGNED') // Sign has its own button
                    .map((nextStatus) => (
                      <button
                        key={nextStatus}
                        onClick={() => updateStatusMutation.mutate({ id: selectedId, status: nextStatus })}
                        disabled={updateStatusMutation.isPending}
                        className={clsx(
                          'inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-medium',
                          STATUS_COLORS[nextStatus]?.replace('bg-', 'border-').split(' ')[0],
                          STATUS_COLORS[nextStatus] || 'border-gray-300 bg-gray-50 text-gray-700',
                          'hover:opacity-80 disabled:opacity-50'
                        )}
                      >
                        Transition to {nextStatus}
                      </button>
                    ))}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-gray-400">Filing data not available.</div>
            )}
          </div>
        )}

        {/* Filing List Table */}
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">All Filings</h3>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : filings.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-lg font-medium">No filings yet</p>
              <p className="mt-1 text-sm">Create your first CFPB filing to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Year</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">LEI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Sequence</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filings.map((f: any) => (
                    <tr key={f.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{f.filingYear}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-mono text-gray-600">{f.lei}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{f.sequenceNumber || '--'}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', STATUS_COLORS[f.status] || STATUS_COLORS.DRAFT)}>
                          {f.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(f.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedId(f.id)}
                            className="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
                          >
                            <EyeIcon className="mr-1 h-3.5 w-3.5" />
                            View
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(f.id)}
                            className="inline-flex items-center rounded px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Filing Modal */}
        <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Create CFPB Filing
              </Dialog.Title>
              <p className="mt-1 text-sm text-gray-500">
                Start a new HMDA filing for the CFPB platform.
              </p>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filing Year</label>
                  <select
                    value={formYear}
                    onChange={(e) => setFormYear(parseInt(e.target.value, 10))}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-teal-500"
                  >
                    {/* HMDA filing covers data for the prior calendar year and is
                        accepted from January through March 1 of the following year.
                        Backend allows 2018-2030; we offer the last 5 valid years. */}
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 - i)
                      .filter((y) => y >= 2018)
                      .map((y) => (
                        <option key={y} value={y}>{y} HMDA data</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">LEI (20 characters)</label>
                  <input
                    type="text"
                    value={formLei}
                    onChange={(e) => setFormLei(e.target.value.toUpperCase())}
                    maxLength={20}
                    placeholder="e.g., 549300ABCDEF1234567G"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:border-teal-500 focus:outline-none focus:ring-teal-500"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    {formLei.length}/20 characters
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Institution Name (optional)</label>
                  <input
                    type="text"
                    value={formInstitutionName}
                    onChange={(e) => setFormInstitutionName(e.target.value)}
                    placeholder="Your institution name"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Linked Validation (optional)
                  </label>
                  <select
                    value={formValidationId}
                    onChange={(e) => setFormValidationId(e.target.value)}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-teal-500"
                  >
                    <option value="">— None (link later) —</option>
                    {teamValidations.map((v: any) => (
                      <option key={v.id} value={v.id}>
                        {v.fileName || v.id.slice(0, 8)} —{' '}
                        {new Date(v.createdAt).toLocaleDateString()}
                        {v.totalRows ? ` (${v.totalRows} rows)` : ''}
                      </option>
                    ))}
                  </select>
                  {teamValidations.length === 0 && (
                    <p className="mt-1 text-xs text-gray-400">
                      No HMDA validations yet — you can link one to this filing later.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => createMutation.mutate()}
                  disabled={!formLei || formLei.length !== 20 || !formYear || createMutation.isPending}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Filing'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-gray-900">Delete Filing</Dialog.Title>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this filing? This action cannot be undone.
              </p>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm)}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </div>
    </FeatureGate>
  );
}
